if (!customElements.get('product-video-bubbles')) {
  customElements.define(
    'product-video-bubbles',
    class ProductVideoBubbles extends HTMLElement {
      constructor() {
        super();
        this.items = [];
        this.video = null;
        this.modal = null;
        this.backdrop = null;
        this.progress = null;
        this.closeButton = null;
        this.prevButton = null;
        this.nextButton = null;
        this.currentIndex = 0;
        this.watched = new Set();
        this.breakpoint = window.matchMedia('(min-width: 750px)');
        this.modalMoved = false;
        this.storageKey = null;
        this.pendingLoadHandler = null;
        this.isClosing = false;

        this.onEscape = this.onEscape.bind(this);
        this.onVideoTimeUpdate = this.onVideoTimeUpdate.bind(this);
        this.onVideoEnded = this.onVideoEnded.bind(this);
        this.onBackdropClick = this.onBackdropClick.bind(this);
        this.onPrev = this.onPrev.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onModalTransitionEnd = this.onModalTransitionEnd.bind(this);
      }

      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-video-bubble-item]'));
        this.video = this.querySelector('[data-video-bubbles-video]');
        this.modal = this.querySelector('[data-video-bubbles-modal]');
        this.backdrop = this.querySelector('[data-video-bubbles-backdrop]');
        this.progress = this.querySelector('[data-video-bubbles-progress]');
        this.closeButton = this.querySelector('[data-video-bubbles-close]');
        this.prevButton = this.querySelector('[data-video-bubbles-prev]');
        this.nextButton = this.querySelector('[data-video-bubbles-next]');

        if (!this.items.length || !this.video || !this.modal || !this.backdrop || !this.progress) return;

        this.storageKey = `pdp-video-bubbles-watched-${this.dataset.productId || window.location.pathname}`;
        this.restoreWatchedState();

        if (!this.modalMoved) {
          document.body.appendChild(this.modal);
          this.modalMoved = true;
        }

        this.items.forEach((item, index) => {
          item.addEventListener('click', () => this.open(index));
        });

        this.video.addEventListener('timeupdate', this.onVideoTimeUpdate);
        this.video.addEventListener('ended', this.onVideoEnded);
        this.backdrop.addEventListener('click', this.onBackdropClick);
        this.modal.addEventListener('transitionend', this.onModalTransitionEnd);
        this.closeButton?.addEventListener('click', this.onClose);
        this.prevButton?.addEventListener('click', this.onPrev);
        this.nextButton?.addEventListener('click', this.onNext);
        document.addEventListener('keydown', this.onEscape);
      }

      disconnectedCallback() {
        this.video?.removeEventListener('timeupdate', this.onVideoTimeUpdate);
        this.video?.removeEventListener('ended', this.onVideoEnded);
        this.backdrop?.removeEventListener('click', this.onBackdropClick);
        this.modal?.removeEventListener('transitionend', this.onModalTransitionEnd);
        this.closeButton?.removeEventListener('click', this.onClose);
        this.prevButton?.removeEventListener('click', this.onPrev);
        this.nextButton?.removeEventListener('click', this.onNext);
        document.removeEventListener('keydown', this.onEscape);

        if (this.modalMoved && this.modal?.parentNode === document.body) {
          this.modal.remove();
          this.modalMoved = false;
        }
      }

      onEscape(event) {
        if (event.key === 'Escape' && this.modal && !this.modal.hidden) {
          this.close();
        }
      }

      onVideoTimeUpdate() {
        const duration = this.video?.duration || 0;
        const currentTime = this.video?.currentTime || 0;
        const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
        this.updateProgress(progress);
      }

      onVideoEnded() {
        this.markWatched(this.currentIndex);

        if (this.currentIndex < this.items.length - 1) {
          this.playIndex(this.currentIndex + 1);
          return;
        }

        this.close();
      }

      onBackdropClick(event) {
        if (!this.breakpoint.matches) return;
        if (event.target === this.backdrop) this.close();
      }

      onPrev() {
        if (this.currentIndex <= 0) return;
        this.playIndex(this.currentIndex - 1);
      }

      onNext() {
        this.markWatched(this.currentIndex);
        if (this.currentIndex >= this.items.length - 1) {
          this.close();
          return;
        }
        this.playIndex(this.currentIndex + 1);
      }

      onClose() {
        this.close();
      }

      onModalTransitionEnd(event) {
        if (event.target !== this.modal || event.propertyName !== 'opacity') return;
        if (this.modal.classList.contains('is-open')) return;
        this.cleanupVideo();
        this.modal.hidden = true;
        this.isClosing = false;
      }

      open(index) {
        if (!this.items[index]) return;

        this.isClosing = false;
        this.modal.hidden = false;
        window.requestAnimationFrame(() => {
          this.modal.classList.add('is-open');
        });
        document.body.classList.add('overflow-hidden');
        this.renderProgressBars();
        this.playIndex(index);
      }

      close() {
        if (!this.modal || this.modal.hidden || this.isClosing) return;

        this.isClosing = true;
        this.modal.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
      }

      cleanupVideo() {
        if (!this.video) return;
        this.video.pause();
        this.video.classList.remove('is-switching');
        this.video.removeAttribute('src');
        this.video.removeAttribute('poster');
        this.video.load();
      }

      getVideoSource(item) {
        const desktopVideo = item.dataset.desktopVideoUrl;
        const mobileVideo = item.dataset.mobileVideoUrl;
        if (this.breakpoint.matches) return desktopVideo || mobileVideo || '';
        return mobileVideo || desktopVideo || '';
      }

      playIndex(index) {
        const item = this.items[index];
        if (!item || !this.video) return;

        const source = this.getVideoSource(item);
        if (!source) {
          this.close();
          return;
        }

        this.video.pause();
        this.currentIndex = index;
        this.video.classList.add('is-switching');
        this.video.controls = false;
        this.video.muted = true;
        this.video.removeAttribute('poster');
        this.video.currentTime = 0;
        this.video.removeAttribute('src');
        this.video.load();

        if (this.pendingLoadHandler) {
          this.video.removeEventListener('loadeddata', this.pendingLoadHandler);
        }
        window.requestAnimationFrame(() => {
          this.video.src = source;
          this.video.load();

          this.pendingLoadHandler = () => {
            window.pauseAllMedia?.();
            const playPromise = this.video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise
                .then(() => {
                  window.requestAnimationFrame(() => {
                    this.video.classList.remove('is-switching');
                  });
                })
                .catch(() => {
                  this.video.classList.remove('is-switching');
                });
            } else {
              this.video.classList.remove('is-switching');
            }
            this.video.removeEventListener('canplay', this.pendingLoadHandler);
            this.pendingLoadHandler = null;
          };
          this.video.addEventListener('canplay', this.pendingLoadHandler, { once: true });
        });

        this.updateProgress(0);
      }

      markWatched(index) {
        const item = this.items[index];
        if (!item) return;
        this.watched.add(index);
        item.classList.add('is-watched');
        this.persistWatchedState();
      }

      renderProgressBars() {
        this.progress.innerHTML = '';
        this.items.forEach((_, index) => {
          const bar = document.createElement('span');
          bar.className = 'product-video-bubbles-modal__progress-bar';
          bar.dataset.progressIndex = `${index}`;

          const fill = document.createElement('span');
          fill.className = 'product-video-bubbles-modal__progress-fill';
          fill.dataset.progressFill = '';

          bar.appendChild(fill);
          this.progress.appendChild(bar);
        });
      }

      updateProgress(currentFillWidth = 0) {
        const bars = Array.from(this.progress.querySelectorAll('[data-progress-index]'));

        bars.forEach((bar, index) => {
          const fill = bar.querySelector('[data-progress-fill]');
          if (!fill) return;

          if (index === this.currentIndex) {
            fill.style.width = `${Math.max(0, Math.min(currentFillWidth, 100))}%`;
            return;
          }

          fill.style.width = this.watched.has(index) ? '100%' : '0%';
        });
      }

      persistWatchedState() {
        if (!this.storageKey) return;
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify(Array.from(this.watched)));
        } catch (error) {
          // no-op when storage is unavailable
        }
      }

      restoreWatchedState() {
        if (!this.storageKey) return;
        try {
          const raw = sessionStorage.getItem(this.storageKey);
          if (!raw) return;
          const watchedIndexes = JSON.parse(raw);
          if (!Array.isArray(watchedIndexes)) return;
          watchedIndexes.forEach((index) => {
            if (!Number.isInteger(index) || !this.items[index]) return;
            this.watched.add(index);
            this.items[index].classList.add('is-watched');
          });
        } catch (error) {
          // no-op when storage is unavailable
        }
      }
    }
  );
}
